import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

/** A hairline that draws itself again on hover or focus of its tile. */
const L = ({ d, delay = 0, dim }: { d: string; delay?: number; dim?: boolean }) => (
  <path d={d} pathLength={1} className={cn("onb-line-hover", dim && "opacity-45")} style={{ ["--onb-delay" as string]: `${delay}ms` }} />
);

const Icon = ({ kind }: { kind: "register" | "risk" | "plans" | "labels" | "escape" }) => (
  <svg viewBox="0 0 64 48" aria-hidden="true" className="h-12 w-16 text-foreground" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round">
    {kind === "register" && (
      <>
        <L d="M14 6h30l6 6v30H14z" />
        <L d="M44 6v6h6" delay={120} />
        <L d="M20 20h6M30 20h14" delay={200} dim />
        <L d="M20 27h6M30 27h14" delay={260} dim />
        <L d="M20 34h6M30 34h10" delay={320} dim />
        <L d="M19 20.2l1.4 1.4 2.4-2.8" delay={380} />
      </>
    )}
    {kind === "risk" && (
      <>
        <L d="M8 42h48" />
        <L d="M18 42V16h10v26" delay={120} />
        <L d="M36 42V28h10v14" delay={220} />
        <L d="M18 24h10" delay={340} dim />
        <L d="M36 34h10" delay={400} dim />
      </>
    )}
    {kind === "plans" && (
      <>
        <L d="M8 8h48v32H8z" />
        <L d="M8 22h22v18M30 8v14M30 28h26" delay={150} dim />
        <L d="M40 15h.01M18 31h.01M44 34h.01" delay={380} />
      </>
    )}
    {kind === "escape" && (
      <>
        <L d="M8 8h48v32H8z" />
        <L d="M8 22h14M30 22h26M30 8v8M30 28v12" delay={120} dim />
        <L d="M14 32h12v-10h14v-8" delay={300} />
        <L d="M46 14h.01" delay={500} />
      </>
    )}
    {kind === "labels" && (
      <>
        <L d="M14 8h36v32H14z" />
        <L d="M20 14h10v10H20zM34 14h10v10H34zM20 28h10v8H20z" delay={140} dim />
        <L d="M34 30h4M42 30v6M34 36h4" delay={300} />
      </>
    )}
  </svg>
);

const KINDS = ["register", "risk", "plans", "labels", "escape"] as const;

/**
 * "Das bekommst du": four small examples of what comes out of the data, each with a hairline that
 * draws itself when you hover or focus it. They are illustrations - no numbers, no screenshots.
 */
export default function ResultTiles() {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="onb-results" data-testid="result-tiles">
      <h2 id="onb-results" className="text-sm font-semibold">{t("onboardingPage.results.title")}</h2>
      <p className="mb-2 text-xs text-muted-foreground">{t("onboardingPage.results.example")}</p>
      <ul className="grid grid-cols-2 gap-2">
        {KINDS.map((kind) => (
          <li key={kind} className={kind === "escape" ? "col-span-2" : undefined}>
            <div
              tabIndex={0}
              className="onb-hover-draw flex h-full items-center gap-3 rounded-2xl border border-border p-3 transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Icon kind={kind} />
              <div className="min-w-0">
                <p className="text-sm font-medium leading-tight">{t(`onboardingPage.results.${kind}.title`)}</p>
                <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{t(`onboardingPage.results.${kind}.text`)}</p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
